import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Warranty {
  id: string;
  property_id: string;
  plant_id: string | null;
  component_type: string;
  manufacturer: string;
  model: string | null;
  serial_numbers: string[] | null;
  warranty_type: string;
  term_years: number | null;
  term_cycles: number | null;
  capacity_floor_percent: number | null;
  coverage_details: string | null;
  install_date: string | null;
  warranty_start_date: string | null;
  warranty_end_date: string | null;
  provider_name: string;
  provider_warranty_id: string | null;
  registration_status: string;
  registration_url: string | null;
  document_paths: string[];
  status: string;
  last_expiry_threshold_days: number | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface WarrantyClaim {
  id: string;
  warranty_id: string;
  property_id: string;
  visit_id: string | null;
  finding_id: string | null;
  issue_id: string | null;
  status: string;
  claim_type: string;
  description: string;
  evidence_summary: string | null;
  submitted_at: string | null;
  decision_at: string | null;
  decision_notes: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  packet_path: string | null;
  correspondence_paths: string[];
  created_at: string;
  updated_at: string;
  created_by: string;
}

const WARRANTIES_KEY = 'warranties';
const WARRANTY_CLAIMS_KEY = 'warranty-claims';

export function useWarranties(propertyId: string | undefined) {
  return useQuery({
    queryKey: [WARRANTIES_KEY, propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('warranties')
        .select('*')
        .eq('property_id', propertyId!)
        .order('component_type')
        .order('warranty_end_date', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as Warranty[];
    },
    enabled: !!propertyId,
  });
}

export function useWarrantyDetail(warrantyId: string | undefined) {
  return useQuery({
    queryKey: [WARRANTIES_KEY, 'detail', warrantyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('warranties')
        .select('*')
        .eq('id', warrantyId!)
        .single();
      if (error) throw error;
      return data as unknown as Warranty;
    },
    enabled: !!warrantyId,
  });
}

export function useWarrantyClaims(warrantyId: string | undefined) {
  return useQuery({
    queryKey: [WARRANTY_CLAIMS_KEY, warrantyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('warranty_claims')
        .select('*')
        .eq('warranty_id', warrantyId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as WarrantyClaim[];
    },
    enabled: !!warrantyId,
  });
}

export function usePropertyWarrantyClaims(propertyId: string | undefined) {
  return useQuery({
    queryKey: [WARRANTY_CLAIMS_KEY, 'property', propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('warranty_claims')
        .select('*')
        .eq('property_id', propertyId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as WarrantyClaim[];
    },
    enabled: !!propertyId,
  });
}

export function useCreateWarranty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: Omit<Warranty, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'last_expiry_threshold_days' | 'status'> & { status?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('warranties')
        .insert({
          ...params,
          status: params.status ?? 'active',
          created_by: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [WARRANTIES_KEY, params.property_id] });
    },
  });
}

export function useUpdateWarranty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; propertyId: string; updates: Partial<Warranty> }) => {
      const { error } = await supabase
        .from('warranties')
        .update({ ...params.updates, updated_at: new Date().toISOString() })
        .eq('id', params.id);
      if (error) throw error;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [WARRANTIES_KEY, params.propertyId] });
      qc.invalidateQueries({ queryKey: [WARRANTIES_KEY, 'detail', params.id] });
    },
  });
}

export function useCreateClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      warranty_id: string;
      property_id: string;
      claim_type: string;
      description: string;
      visit_id?: string;
      finding_id?: string;
      issue_id?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('warranty_claims')
        .insert({
          warranty_id: params.warranty_id,
          property_id: params.property_id,
          claim_type: params.claim_type,
          description: params.description,
          visit_id: params.visit_id ?? null,
          finding_id: params.finding_id ?? null,
          issue_id: params.issue_id ?? null,
          created_by: user.id,
        })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [WARRANTY_CLAIMS_KEY, params.warranty_id] });
      qc.invalidateQueries({ queryKey: [WARRANTY_CLAIMS_KEY, 'property', params.property_id] });
    },
  });
}

export function useUpdateClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; warrantyId: string; propertyId: string; updates: Partial<WarrantyClaim> }) => {
      const { error } = await supabase
        .from('warranty_claims')
        .update({ ...params.updates, updated_at: new Date().toISOString() })
        .eq('id', params.id);
      if (error) throw error;
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [WARRANTY_CLAIMS_KEY, params.warrantyId] });
      qc.invalidateQueries({ queryKey: [WARRANTY_CLAIMS_KEY, 'property', params.propertyId] });
    },
  });
}

export function useGenerateClaimPacket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { claimId: string; warrantyId: string; propertyId: string }) => {
      const { data, error } = await supabase.functions.invoke('generate-claim-packet', {
        body: { claimId: params.claimId },
      });
      if (error) throw error;
      return data as { ok: boolean; packet_path: string };
    },
    onSuccess: (_data, params) => {
      qc.invalidateQueries({ queryKey: [WARRANTY_CLAIMS_KEY, params.warrantyId] });
      qc.invalidateQueries({ queryKey: [WARRANTY_CLAIMS_KEY, 'property', params.propertyId] });
    },
  });
}

/** Helper: days remaining until warranty end date. Negative = expired. */
export function warrantyDaysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  const end = new Date(endDate);
  const now = new Date();
  return Math.floor((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/** Helper: group warranties by component_type */
export function groupWarrantiesByComponent(warranties: Warranty[]): Record<string, Warranty[]> {
  const groups: Record<string, Warranty[]> = {};
  for (const w of warranties) {
    if (!groups[w.component_type]) groups[w.component_type] = [];
    groups[w.component_type].push(w);
  }
  return groups;
}
